import { MAPBOX_STYLE_URL } from '@/app/constants'
import { TPortConfig } from '@/app/config/ports'

const EARTH_CIRCUMFERENCE_M = 2 * Math.PI * 6371 * 1000
const MAPBOX_TILE_SIZE = 512
const MIN_CIRCLE_RADIUS_PX = 5

export const STATIC_MAP_WIDTH = 250
export const STATIC_MAP_HEIGHT = 500

export function buildStaticMapUrl(lon: number, lat: number, zoom: number, token: string): string {
  const stylePath = MAPBOX_STYLE_URL.replace('mapbox://styles/', '')
  return `https://api.mapbox.com/styles/v1/${stylePath}/static/${lon},${lat},${zoom}/${STATIC_MAP_WIDTH}x${STATIC_MAP_HEIGHT}@2x?access_token=${token}`
}

export function getMetersPerPixel(lat: number, zoom: number): number {
  return (
    (Math.cos((lat * Math.PI) / 180) * EARTH_CIRCUMFERENCE_M) /
    (MAPBOX_TILE_SIZE * Math.pow(2, zoom))
  )
}

export function getCircleRadiusPx(lat: number, radiusKm: number, zoom: number): number {
  return Math.max((radiusKm * 1000) / getMetersPerPixel(lat, zoom), MIN_CIRCLE_RADIUS_PX)
}

export function getBoundsView(
  minLon: number,
  minLat: number,
  maxLon: number,
  maxLat: number,
  padding = 60
): { lon: number; lat: number; zoom: number } {
  const centerLon = (minLon + maxLon) / 2
  const centerLat = (minLat + maxLat) / 2
  const availWidth = STATIC_MAP_WIDTH - 2 * padding
  const availHeight = STATIC_MAP_HEIGHT - 2 * padding

  for (let z = 14; z >= 0; z--) {
    const mpp = getMetersPerPixel(centerLat, z)
    const lonMeters =
      ((maxLon - minLon) * Math.PI * 6371 * 1000 * Math.cos((centerLat * Math.PI) / 180)) / 180
    const latMeters = ((maxLat - minLat) * Math.PI * 6371 * 1000) / 180
    if (lonMeters / mpp <= availWidth && latMeters / mpp <= availHeight) {
      return { lon: centerLon, lat: centerLat, zoom: z }
    }
  }

  return { lon: centerLon, lat: centerLat, zoom: 0 }
}

export function getVesselPixelPosition(
  mapLon: number,
  mapLat: number,
  vesselLon: number,
  vesselLat: number,
  zoom: number
): { x: number; y: number } {
  const mpp = getMetersPerPixel(mapLat, zoom)
  const dLonMeters =
    ((vesselLon - mapLon) * Math.PI * 6371 * 1000 * Math.cos((mapLat * Math.PI) / 180)) / 180
  const dLatMeters = ((vesselLat - mapLat) * Math.PI * 6371 * 1000) / 180
  return {
    x: STATIC_MAP_WIDTH / 2 + dLonMeters / mpp,
    y: STATIC_MAP_HEIGHT / 2 - dLatMeters / mpp
  }
}

export function resolveMapView(
  vesselLon: number | null,
  vesselLat: number | null,
  portConfig: TPortConfig | null,
  defaultZoom: number
): { lon: number; lat: number; zoom: number } {
  const baseLon = vesselLon ?? 0
  const baseLat = vesselLat ?? 0

  if (!portConfig) {
    return { lon: baseLon, lat: baseLat, zoom: defaultZoom }
  }

  const lons = portConfig.polygonCoordinates.map(([lon]) => lon)
  const lats = portConfig.polygonCoordinates.map(([, lat]) => lat)

  if (vesselLon != null && vesselLat != null) {
    lons.push(vesselLon)
    lats.push(vesselLat)
  }

  return getBoundsView(Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats))
}

export function resolveVesselPosition(
  mapLon: number,
  mapLat: number,
  vesselLon: number,
  vesselLat: number,
  mapZoom: number,
  isPortView: boolean
): { x: number; y: number } {
  if (!isPortView) {
    return { x: STATIC_MAP_WIDTH / 2, y: STATIC_MAP_HEIGHT / 2 }
  }
  return getVesselPixelPosition(mapLon, mapLat, vesselLon, vesselLat, mapZoom)
}
